import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, FileText, Sparkles, AlertCircle, RefreshCw, Mail, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import analytics from "@/utils/analytics";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL = 2000; // Check every 2 seconds
const TIMEOUT_DURATION = 20000; // Timeout after 20 seconds

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State
  const [stage, setStage] = useState("initializing"); // initializing, checking, unlocked, timeout, error
  const [submissionId, setSubmissionId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState({});
  
  // Refs for cleanup
  const hasTrackedPayment = useRef(false);
  const pollIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const isInitialized = useRef(false);

  // Helper: Get payment session from storage
  const getPaymentSession = () => {
    try {
      // Try complete payment session first
      const sessionData = localStorage.getItem('proofix_payment_session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        console.log('[PaymentSuccess] Found payment session:', session);
        return session;
      }
      
      // Fallback to simple submission ID
      const simpleId = localStorage.getItem('pending_payment_submission');
      if (simpleId) {
        console.log('[PaymentSuccess] Found simple submission ID:', simpleId);
        return { submissionId: simpleId };
      }
      
      return null;
    } catch (e) {
      console.error('[PaymentSuccess] Error reading payment session:', e);
      return null;
    }
  };

  // Helper: Clear payment session
  const clearPaymentSession = () => {
    localStorage.removeItem('proofix_payment_session');
    localStorage.removeItem('pending_payment_submission');
    console.log('[PaymentSuccess] Cleared payment session');
  };

  // Initialize: Find submission ID from URL or storage
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    console.log('[PaymentSuccess] ====== INITIALIZING ======');
    console.log('[PaymentSuccess] URL params:', Object.fromEntries(searchParams));
    
    // Strategy 1: Try URL parameter
    let subId = searchParams.get("submission_id");
    let email = searchParams.get("email");
    
    console.log('[PaymentSuccess] From URL - submissionId:', subId, ', email:', email);
    
    // Strategy 2: Try localStorage payment session
    if (!subId) {
      const session = getPaymentSession();
      if (session) {
        subId = session.submissionId;
        email = email || session.email;
        console.log('[PaymentSuccess] From storage - submissionId:', subId, ', email:', email);
      }
    }
    
    // Store debug info
    setDebugInfo({
      urlParams: Object.fromEntries(searchParams),
      storageSession: getPaymentSession(),
      resolvedSubmissionId: subId,
      resolvedEmail: email
    });
    
    if (subId) {
      setSubmissionId(subId);
      setUserEmail(email);
      setStage("checking");
      console.log('[PaymentSuccess] Starting polling for submission:', subId);
    } else if (email) {
      // No submission ID but we have email - try to find by email
      setUserEmail(email);
      setStage("checking");
      console.log('[PaymentSuccess] No submissionId, will search by email:', email);
    } else {
      // No submission ID and no email - show error but also try email lookup
      console.log('[PaymentSuccess] No submissionId or email found');
      setStage("error");
      setError("We konden uw betaling niet direct koppelen. Als u betaald heeft, ontvangt u binnen enkele minuten een e-mail met uw rapport.");
    }
  }, [searchParams]);

  // Polling logic
  useEffect(() => {
    if (stage !== "checking") return;
    
    // Track payment success (only once)
    if (!hasTrackedPayment.current && submissionId) {
      analytics.paymentSuccess(submissionId, 1900);
      hasTrackedPayment.current = true;
    }

    // Function to check premium status
    const checkPremiumStatus = async () => {
      try {
        let foundSubmission = null;
        
        // Try direct submission lookup first
        if (submissionId) {
          console.log('[PaymentSuccess] Checking submission:', submissionId);
          try {
            const response = await axios.get(`${API_URL}/submissions/${submissionId}`);
            foundSubmission = response.data;
          } catch (e) {
            console.log('[PaymentSuccess] Direct lookup failed:', e.message);
          }
        }
        
        // If no direct submission or not unlocked, try email lookup
        if ((!foundSubmission || !foundSubmission.premium_unlocked) && userEmail) {
          console.log('[PaymentSuccess] Trying email lookup:', userEmail);
          try {
            const response = await axios.get(`${API_URL}/payment/lookup-by-email`, {
              params: { email: userEmail }
            });
            if (response.data && response.data.premium_unlocked) {
              foundSubmission = response.data;
              setSubmissionId(response.data.id);
              console.log('[PaymentSuccess] Found paid submission by email:', response.data.id);
            }
          } catch (e) {
            console.log('[PaymentSuccess] Email lookup failed:', e.message);
          }
        }
        
        if (foundSubmission && foundSubmission.premium_unlocked === true) {
          // Premium is unlocked! Clear intervals and redirect
          console.log('[PaymentSuccess] SUCCESS - Premium unlocked!');
          clearInterval(pollIntervalRef.current);
          clearTimeout(timeoutRef.current);
          clearPaymentSession();
          setStage("unlocked");
          
          // Short delay to show success state, then redirect
          setTimeout(() => {
            navigate(`/dba-check/rapport/${foundSubmission.id}`);
          }, 1500);
          
          return true;
        }
        
        setPollCount(prev => prev + 1);
        console.log('[PaymentSuccess] Poll', pollCount + 1, '- Not unlocked yet');
        return false;
      } catch (err) {
        console.error("[PaymentSuccess] Error checking status:", err);
        return false;
      }
    };

    // Initial check
    checkPremiumStatus();

    // Start polling every 2 seconds
    pollIntervalRef.current = setInterval(() => {
      checkPremiumStatus();
    }, POLL_INTERVAL);

    // Set timeout fallback (20 seconds)
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollIntervalRef.current);
      console.log('[PaymentSuccess] Timeout reached after', TIMEOUT_DURATION, 'ms');
      setStage("timeout");
    }, TIMEOUT_DURATION);

    // Cleanup on unmount
    return () => {
      clearInterval(pollIntervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [stage, submissionId, userEmail, navigate, pollCount]);

  // Manual retry function - uses mobile recovery endpoint for best results
  const handleManualRetry = async () => {
    setStage("checking");
    setPollCount(0);
    
    // Re-initialize with mobile recovery endpoint
    const checkOnce = async () => {
      try {
        // First try mobile recovery endpoint (handles all strategies)
        console.log('[PaymentSuccess] Trying mobile recovery endpoint...');
        const recoveryResponse = await axios.get(`${API_URL}/payment/mobile-recovery`, {
          params: { 
            email: userEmail || undefined,
            submission_id: submissionId || undefined
          }
        });
        
        if (recoveryResponse.data.status === "found") {
          const { submission, access_urls } = recoveryResponse.data;
          console.log('[PaymentSuccess] Mobile recovery found submission:', submission.id);
          
          clearPaymentSession();
          setSubmissionId(submission.id);
          setStage("unlocked");
          
          // Use the secure token URL for redirect (most reliable)
          setTimeout(() => {
            navigate(`/dba-check/rapport/${submission.id}`);
          }, 1500);
          return;
        }
        
        // Fallback: Direct submission lookup
        if (submissionId) {
          const response = await axios.get(`${API_URL}/submissions/${submissionId}`);
          if (response.data.premium_unlocked === true) {
            clearPaymentSession();
            setStage("unlocked");
            setTimeout(() => {
              navigate(`/dba-check/rapport/${submissionId}`);
            }, 1500);
            return;
          }
        }
        
        // Fallback: Email lookup
        if (userEmail) {
          const response = await axios.get(`${API_URL}/payment/lookup-by-email`, {
            params: { email: userEmail }
          });
          if (response.data && response.data.premium_unlocked) {
            clearPaymentSession();
            setSubmissionId(response.data.id);
            setStage("unlocked");
            setTimeout(() => {
              navigate(`/dba-check/rapport/${response.data.id}`);
            }, 1500);
            return;
          }
        }
        
        setStage("timeout");
      } catch (e) {
        console.error('[PaymentSuccess] Recovery failed:', e);
        setStage("timeout");
      }
    };
    
    await checkOnce();
  };

  // Error state - offer email input for mobile recovery
  if (stage === "error") {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-white mb-4">
            Betaling wordt verwerkt
          </h1>
          <p className="text-zinc-400 mb-6 leading-relaxed">
            {error}
          </p>
          
          {/* Email input for mobile recovery */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5 mb-6">
            <p className="text-zinc-300 text-sm mb-3 text-left">
              <strong>Heeft u betaald?</strong> Voer uw e-mailadres in om uw rapport op te halen:
            </p>
            <input
              type="email"
              placeholder="uw@email.nl"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 mb-3"
              value={userEmail || ""}
              onChange={(e) => setUserEmail(e.target.value)}
              data-testid="recovery-email-input"
            />
            <Button
              onClick={handleManualRetry}
              className="w-full rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white"
              data-testid="recovery-email-btn"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Rapport ophalen
            </Button>
          </div>
          
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 mb-6 text-left">
            <p className="text-zinc-400 text-sm">
              <strong className="text-zinc-300">Tip:</strong> Check ook uw e-mail inbox (en spam folder) voor een directe link naar uw rapport.
            </p>
          </div>
          
          <Link to="/dba-check">
            <Button
              variant="outline"
              className="w-full rounded-full px-8 py-6 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Home className="w-5 h-5 mr-2" />
              Terug naar DBA Check
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Timeout state - payment not confirmed yet
  if (stage === "timeout") {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center max-w-md">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-8"
          >
            <Loader2 className="w-12 h-12 text-amber-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-outfit text-2xl md:text-3xl font-medium text-white mb-4"
          >
            Betaling wordt verwerkt...
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-zinc-400 mb-6 leading-relaxed"
          >
            Uw betaling is ontvangen, maar we verwerken deze nog. 
            U ontvangt zo direct toegang of een e-mail met uw rapport.
          </motion.p>

          {/* Email recovery section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5 mb-6"
          >
            <p className="text-zinc-300 text-sm mb-3 text-left">
              <strong>Mobiele gebruiker?</strong> Voer uw e-mailadres in:
            </p>
            <input
              type="email"
              placeholder="uw@email.nl"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 mb-3"
              value={userEmail || ""}
              onChange={(e) => setUserEmail(e.target.value)}
              data-testid="timeout-email-input"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <Button
              onClick={handleManualRetry}
              className="w-full rounded-full px-8 py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white font-semibold text-lg"
              data-testid="retry-btn"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Opnieuw controleren
            </Button>
            
            {submissionId && (
              <Button
                onClick={() => navigate(`/dba-check/resultaat/${submissionId}`)}
                variant="outline"
                className="w-full rounded-full px-8 py-6 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="back-to-result-btn"
              >
                Terug naar resultaat
              </Button>
            )}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-zinc-500 text-sm mt-8"
          >
            Check ook uw e-mail inbox voor een directe link naar uw rapport.
          </motion.p>
        </div>
      </div>
    );
  }

  // Unlocked state - premium is confirmed
  if (stage === "unlocked") {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center max-w-md">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-8"
            data-testid="success-icon"
          >
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-outfit text-3xl md:text-4xl font-medium text-white mb-4"
            data-testid="success-title"
          >
            Betaling geslaagd!
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-center gap-3 text-emerald-400 mb-8"
          >
            <FileText className="w-5 h-5" />
            <span>Uw rapport is ontgrendeld!</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden mb-8"
          >
            <motion.div
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-zinc-400 text-sm"
          >
            U wordt doorgestuurd naar uw rapport...
          </motion.p>
        </div>
      </div>
    );
  }

  // Default: Initializing or Checking state (polling)
  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 text-center max-w-md">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-24 h-24 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-8"
          data-testid="checking-icon"
        >
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-outfit text-3xl md:text-4xl font-medium text-white mb-4"
        >
          Betaling wordt verwerkt...
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center gap-3 text-zinc-300">
            <Sparkles className="w-5 h-5 text-blue-400" />
            <span>We controleren uw betaling...</span>
          </div>
        </motion.div>

        {/* Animated progress dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center justify-center gap-2 mb-8"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-3 h-3 rounded-full bg-blue-500"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-zinc-500 text-sm"
        >
          Dit duurt meestal maar een paar seconden
        </motion.p>
        
        {/* Debug info for development */}
        {pollCount > 3 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-zinc-600 text-xs mt-4"
          >
            Poging {pollCount}...
          </motion.p>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
